// Phase MHM-4 · Courier Partner UI — partner cards + SLA + pricing tool + failed alerts.
import { useEffect, useState, useCallback } from "react";
import { Loader2, RefreshCw, Plug, Truck } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend } from "recharts";
import { toast } from "sonner";
import api from "../../services/api";
import MHMSubNav from "../../components/hub/MHMSubNav";
import SlaAlertBadge from "../../components/hub/SlaAlertBadge";
import RoleGate from "../../components/RoleGate";

const TABS = ["Partners", "SLA Comparison", "Pricing Tool", "Failed Alerts"];

function PartnerCard({ p, onTest }) {
  const [testing, setTesting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const handleTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post(`/hub/couriers/${p.partner_code}/test-connection`);
      setLastResult(data);
      toast.success(`${p.display_name}: ${data.latency_ms}ms`);
      onTest();
    } catch (e) { toast.error(e.userMessage || "Test failed"); }
    finally { setTesting(false); }
  };
  const healthColor = {ok: "bg-green-500", degraded: "bg-amber-500", down: "bg-rose-500"}[p.health] || "bg-slate-400";
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`mhm-courier-card-${p.partner_code}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500">{p.partner_code}</div>
          <h3 className="text-lg font-semibold text-slate-900 font-['Outfit']">{p.display_name}</h3>
          <div className="flex items-center gap-2 mt-1 text-xs">
            <span className={`px-2 py-0.5 rounded-full ${p.status === "mock" ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-800"} font-semibold uppercase`}>
              {p.status}
            </span>
            <span className="inline-flex items-center gap-1">
              <span className={`h-2 w-2 rounded-full ${healthColor}`}></span>
              {p.health}
            </span>
          </div>
          <div className="text-xs text-slate-500 mt-2">SLA class: <b>{p.sla_class}</b></div>
          <div className="text-xs text-slate-500">Base: ₹{p.base_pricing}</div>
          {p.last_call_at && (
            <div className="text-xs text-slate-400">Last call: {p.last_call_at.slice(0, 19).replace("T", " ")}</div>
          )}
        </div>
        <Truck className="h-6 w-6 text-violet-400" />
      </div>
      <div className="flex gap-2 mt-3">
        <button onClick={handleTest} disabled={testing}
                data-testid={`mhm-courier-test-${p.partner_code}`}
                className="text-xs px-3 py-1.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white rounded-lg inline-flex items-center gap-1">
          <Plug className="h-3 w-3" />{testing ? "Testing…" : "Test connection"}
        </button>
        <button disabled
                title={`Set env: ${p.partner_code.toUpperCase()}_API_KEY`}
                data-testid={`mhm-courier-configure-${p.partner_code}`}
                className="text-xs px-3 py-1.5 border border-slate-200 text-slate-400 cursor-not-allowed rounded-lg">
          Configure live keys
        </button>
      </div>
      {lastResult && (
        <div className="mt-2 text-xs text-slate-500" data-testid={`mhm-courier-result-${p.partner_code}`}>
          ✓ {lastResult.latency_ms}ms · {lastResult.note}
        </div>
      )}
    </div>
  );
}

export default function CourierPartners() {
  const [partners, setPartners] = useState([]);
  const [sla, setSla] = useState([]);
  const [failed, setFailed] = useState([]);
  const [tab, setTab] = useState("Partners");
  const [loading, setLoading] = useState(true);
  const [weight, setWeight] = useState(2);
  const [distance, setDistance] = useState(50);
  const [pinPair, setPinPair] = useState("");
  const [prices, setPrices] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s, f] = await Promise.all([
        api.get("/hub/couriers/partners"),
        api.get("/hub/couriers/sla-comparison"),
        api.get("/hub/couriers/failed-alerts"),
      ]);
      setPartners(p.data.partners || []);
      setSla(s.data.rows || []);
      setFailed(f.data.rows || []);
    } catch (e) { toast.error(e.userMessage || "Load failed"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const compare = async () => {
    if (!weight || weight <= 0) return toast.error("Weight > 0");
    try {
      const { data } = await api.get(
        `/hub/couriers/pricing-comparison?weight_kg=${weight}&distance_km=${distance}&pincode_pair=${pinPair}`);
      setPrices(data);
    } catch (e) { toast.error(e.userMessage || "Compare failed"); }
  };

  return (
    <RoleGate allow={["main_hub_manager", "admin", "founder", "ceo", "micro_hub_lead"]}>
      <MHMSubNav />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="mhm-couriers-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Outfit']" data-testid="mhm-couriers-title">
              Courier Partners
            </h1>
            <p className="text-sm text-slate-500">{partners.length} configured · all MOCK in preview</p>
          </div>
          <div className="flex items-center gap-2">
            <SlaAlertBadge />
            <button onClick={load} data-testid="mhm-couriers-refresh"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 hover:bg-slate-50 rounded-lg">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh
            </button>
          </div>
        </div>

        <div className="flex gap-1 flex-wrap" data-testid="mhm-couriers-tabs">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)}
                    data-testid={`mhm-couriers-tab-${t.replace(/\s/g, "-").toLowerCase()}`}
                    className={`text-sm px-3 py-1.5 rounded-full ${
                      tab === t ? "bg-violet-600 text-white" : "bg-slate-100 hover:bg-slate-200 text-slate-700"
                    }`}>{t}</button>
          ))}
        </div>

        {tab === "Partners" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="mhm-couriers-grid">
            {partners.map((p) => (
              <PartnerCard key={p.partner_code} p={p} onTest={load} />
            ))}
          </div>
        )}

        {tab === "SLA Comparison" && (
          <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="mhm-couriers-sla">
            <h3 className="font-semibold text-sm mb-2">SLA · last 30 days · {sla.length} partners with manifests</h3>
            <div className="h-[280px]">
              <ResponsiveContainer>
                <BarChart data={sla}>
                  <CartesianGrid stroke="#eef2f7" />
                  <XAxis dataKey="partner_code" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="on_time_pct" fill="#22c55e" name="On-time %" />
                  <Bar dataKey="failed_pct" fill="#ef4444" name="Failed %" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            {sla.length === 0 && <div className="text-sm text-slate-400 text-center py-6">No manifests with courier_partner in range</div>}
          </div>
        )}

        {tab === "Pricing Tool" && (
          <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3" data-testid="mhm-couriers-pricing">
            <div className="flex flex-wrap gap-2 items-end">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Weight (kg)</label>
                <input type="number" step="0.1" value={weight} onChange={(e) => setWeight(Number(e.target.value))}
                       data-testid="mhm-courier-price-weight"
                       className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 w-32"/>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Distance (km)</label>
                <input type="number" value={distance} onChange={(e) => setDistance(Number(e.target.value))}
                       className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 w-32"/>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Pincode pair</label>
                <input value={pinPair} onChange={(e) => setPinPair(e.target.value)}
                       placeholder="560001-110001"
                       className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 w-44"/>
              </div>
              <button onClick={compare}
                      data-testid="mhm-courier-price-compare"
                      className="text-sm px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg">
                Compare
              </button>
            </div>
            {prices && (
              <table className="w-full text-sm" data-testid="mhm-courier-price-results">
                <thead className="text-xs uppercase text-slate-500"><tr><th className="text-left p-1">Partner</th><th className="text-right">Price</th><th className="text-right">ETA</th><th className="text-right">Savings vs max</th></tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {prices.rows.map((r, i) => (
                    <tr key={r.partner_code} className={i === 0 ? "bg-green-50" : ""}>
                      <td className="p-1">{i === 0 && "🥇 "}{r.partner_code}</td>
                      <td className="text-right font-mono">₹{r.price_estimate}</td>
                      <td className="text-right text-xs">{r.eta_hours}h</td>
                      <td className="text-right text-xs text-green-600">{r.savings_vs_max_pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === "Failed Alerts" && (
          <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="mhm-couriers-failed">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 bg-slate-50">
                <tr><th className="text-left p-1">Kind</th><th className="text-left">Manifest / Type</th><th className="text-left">Partner</th><th className="text-left">Status</th><th className="text-left">Created</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {failed.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">No failed alerts</td></tr>}
                {failed.map((f, i) => (
                  <tr key={i}>
                    <td className="p-1 text-xs">{f.kind}</td>
                    <td className="font-mono text-xs">{f.manifest_id || f.type}</td>
                    <td className="text-xs">{f.partner_code || "—"}</td>
                    <td className="text-xs">{f.status || f.severity}</td>
                    <td className="text-xs text-slate-500">{(f.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </RoleGate>
  );
}
